import { CPLAT_API_ROUTES, cplatApiV3, orderQueries, usePost } from '@/api'; // cplatApiV2 인스턴스 사용
import { Dialog, DialogClose, DialogContent } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/NewPcButton';
import { useLog } from '@/hooks/V3/useLog';
import { useToast } from '@/hooks/V3/useToast';
import { startLoading, stopLoading } from '@/redux/loadingSlice';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { deviceUuidState } from '@/store/atom';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useOrderDetails } from '../../OrderDetailsProvider';

interface ConfirmOrderResult {
  success: boolean;
  orderNo: string;
  error?: unknown;
  message?: string;
}

interface Props {
  setConfirmOrderOpen: (open: boolean) => void;
}

export const ConfirmOrderController = ({ setConfirmOrderOpen }: Props) => {
  const { checkedOrders } = useOrderDetails();
  const handleConfirmOrders = () => setConfirmOrderOpen(true);

  return (
    <Button
      className="w-fit gap-1"
      variant="solid"
      size="small"
      disabled={checkedOrders.length === 0}
      onClick={handleConfirmOrders}
    >
      주문 확인
      {checkedOrders.length > 0 && <span className="Body5M14">{checkedOrders.length}</span>}
    </Button>
  );
};

export const ConfirmOrderModal = ({
  confirmOrderOpen,
  setConfirmOrderOpen,
}: {
  confirmOrderOpen: boolean;
  setConfirmOrderOpen: (open: boolean) => void;
}) => {
  const { showToast } = useToast();

  const dispatch = useAppDispatch();
  const { checkedOrders, setCheckedOrders } = useOrderDetails();
  const user = useAppSelector((state) => state.user);
  const { setUserEventHistory } = useLog(user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);

  const [cancelledOrderNos, setCancelledOrderNos] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchParams] = useSearchParams();

  const { mutate: syncOrders } = usePost(CPLAT_API_ROUTES.syncOrders, undefined, undefined, true);
  const queryClient = useQueryClient();

  const from = searchParams.get('from');
  const to = searchParams.get('to');

  const handleUpdate = () => {
    setUserEventHistory({
      eventName: 'order_collection_button_click_web_dashboard',
      eventValue: '주문수집버튼_클릭',
      deviceUuid,
    });

    const requestBody = {
      cplatToken: user.CplatToken,
      dateFrom: from ?? moment(),
      dateTo: to ?? moment(),
    };

    syncOrders(requestBody, {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: orderQueries.all() });

        showToast('success', '주문 수집이 완료되었습니다.');
      },
      onError(error) {
        console.log(error);
      },
    });
  };

  // 주문확인 처리 함수 (cplatApiV2 사용)
  const handleConfirmOrders = async () => {
    if (checkedOrders.length === 0) return;
    setIsProcessing(true);
    dispatch(startLoading());

    const confirmOrderParamsArray = checkedOrders.map((order) => {
      return {
        cplatToken: user.CplatToken,
        marketId: order.marketId,
        storeId: order.storeId || '',
        orderNo: order.orderNo,
        orderItemList: [order.orderItemNo],
        coupangShipmentBoxList: order.coupangShipmentBoxNo ? [order.coupangShipmentBoxNo] : [],
        TmallDeliveryNo: order.tmallDeliveryNo,
        ProductNo: order.productNo,
        Quantity: order.orderCount,
      };
    });

    // 여기서 주문 번호 리스트, 주문 개수 로그 기록
    setUserEventHistory({
      eventName: 'confirm_order_execute_web_dashboard',
      eventValue: JSON.stringify({
        orderCount: checkedOrders.length,
        orderNos: checkedOrders.map((o) => o.orderNo),
      }),
      deviceUuid,
    });

    const results: ConfirmOrderResult[] = await Promise.all(
      confirmOrderParamsArray.map(async (params) => {
        try {
          const res = await cplatApiV3.post('/order/confirm_order', params);
          const code = res.data?.code;

          return { success: code == '200', orderNo: params.orderNo, message: res.data?.message };
        } catch (error: any) {
          console.error(error, 'cplatApiV2 error');
          return { success: false, orderNo: params.orderNo, error };
        }
      }),
    );

    const cancelled = results.filter((r) => !r.success).map((r) => r.orderNo);

    if (cancelled.length === 0) {
      showToast('success', '주문 확인 처리가 완료되었어요');
      setCheckedOrders([]);
      setConfirmOrderOpen(false);
    } else {
      setCancelledOrderNos(cancelled);
      setCheckedOrders([]);
      showToast('warning', '주문 확인 처리 중 일부 주문이 구매자 취소로 처리되었어요');
      setConfirmOrderOpen(false);
      handleUpdate();
    }

    queryClient.invalidateQueries({ queryKey: orderQueries.all() });

    setIsProcessing(false);
    dispatch(stopLoading());
  };

  const handleModalClose = (open: boolean) => {
    setConfirmOrderOpen(open);
    if (!open) {
      setCancelledOrderNos([]);
    }
    queryClient.invalidateQueries({ queryKey: orderQueries.all() });
  };

  if (cancelledOrderNos.length === 0) {
    return (
      <Dialog open={confirmOrderOpen} onOpenChange={handleModalClose}>
        <DialogContent noCloseBtn className="w-[380px] p-[30px]">
          <div className="flex flex-col justify-between">
            <div className="flex flex-col gap-1">
              <div className="text-gr-900 Title3S18">주문 확인을 진행할까요?</div>
              <div className="text-gr-700 Body5M14">
                구매자에게 상품 준비중 또는 배송 대기중 상태로 보여져요
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <DialogClose asChild>
                <Button
                  className="h-[44px] flex-1"
                  variant="muted"
                  onClick={() => handleModalClose(false)}
                  disabled={isProcessing}
                >
                  닫기
                </Button>
              </DialogClose>
              <Button className="flex-1" onClick={handleConfirmOrders} disabled={isProcessing}>
                확인
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={confirmOrderOpen} onOpenChange={handleModalClose}>
      <DialogContent noCloseBtn className="w-[380px] p-[30px]">
        <div className="flex flex-col ">
          <div className="text-gr-900 Title3S18">구매자가 결제를 취소했어요</div>
          <div className=" text-gr-700 Body5M14">구매자 요청에 의해 결제 취소 처리됐어요.</div>
          <div className="mt-1 flex w-full flex-col  gap-1">
            <ul className="flex w-full list-disc flex-col gap-1 pl-5">
              {cancelledOrderNos.map((no) => (
                <li key={no} className="text-gr-700 Body5M14">
                  {no}
                </li>
              ))}
            </ul>
          </div>
          <Button className="mt-[20px] h-[44px] w-full" onClick={() => handleModalClose(false)}>
            확인
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
